package com.hyperether.goodjob.scenes.scheduler

import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.lifecycle.viewModelScope
import com.hyperether.goodjob.Dp
import com.hyperether.goodjob.getPlatformChecker
import com.hyperether.goodjob.log
import com.hyperether.goodjob.models.Job
import com.hyperether.goodjob.repository.Repository
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.today
import com.hyperether.goodjob.scenes.JobsViewModel
import com.hyperether.goodjob.util.DateTimeUtil
import com.hyperether.goodjob.util.ScheduleUtil
import com.hyperether.planner.model.Event
import com.hyperether.planner.model.Plan
import com.hyperether.resources.stringResourcePlain
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import kotlinx.datetime.toLocalDateTime
import kotlinx.datetime.todayIn

class SchedulerViewModel(repository: Repository) : JobsViewModel(repository) {

    val initialLoad = mutableStateOf(true)
    var periodState = mutableStateOf(PeriodType.DAY)
    var currentPeriod = mutableStateOf("")
    private var selectedDay: LocalDate = Clock.System.todayIn(TimeZone.currentSystemDefault())
    private val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
    var listOfPlans = mutableStateListOf<Plan>()

    var selectedJobWeeklyHover = mutableStateOf(false)
    var hoveredEvent = mutableStateOf<Event?>(null)
    var position = mutableStateOf(Position(Dp(0f), Dp(0f)))
    var currentJobs = mutableStateListOf<Job>()
    var monthSelectedDay = mutableStateOf<Int?>(null)

    var weekSelectionStart = mutableStateOf(
        Clock.System.now()
            .toLocalDateTime(TimeZone.currentSystemDefault()).date.minus(DatePeriod(days = today.dayOfWeek.ordinal))
    )
    var weekSelectionEnd = mutableStateOf(
        Clock.System.now()
            .toLocalDateTime(TimeZone.currentSystemDefault()).date.plus(DatePeriod(days = DayOfWeek.SUNDAY.ordinal - today.dayOfWeek.ordinal))
    )
    var monthSelectionLocalDate =
        mutableStateOf(Clock.System.todayIn(TimeZone.currentSystemDefault()))

    init {
        tag = "SchedulerViewModel"
        if (getPlatformChecker().isMobile())
            init()
    }

    fun init() {
        resetFilter()
        initViewModel()
        if (getPlatformChecker().isMobile()) {
            loadRemoteData()
        }

        viewModelScope.launch {
            currentPeriod.value = stringResourcePlain(Res.string.today)
            periodState.value = PeriodType.DAY
            selectedDay = Clock.System.todayIn(TimeZone.currentSystemDefault())
            monthSelectedDay = mutableStateOf(null)
            monthSelectionLocalDate =
                mutableStateOf(Clock.System.todayIn(TimeZone.currentSystemDefault()))
            weekSelectionStart = mutableStateOf(
                Clock.System.now()
                    .toLocalDateTime(TimeZone.currentSystemDefault()).date.minus(DatePeriod(days = today.dayOfWeek.ordinal))
            )
            weekSelectionEnd = mutableStateOf(
                Clock.System.now()
                    .toLocalDateTime(TimeZone.currentSystemDefault()).date.plus(DatePeriod(days = DayOfWeek.SUNDAY.ordinal - today.dayOfWeek.ordinal))
            )
        }

        viewModelScope.launch {
            repository.getJobsFlow().collect {
                delay(250)
                parseSchedule()
            }
        }

        viewModelScope.launch {
            repository.getUsersFlow().collect {
                delay(250)
                parseSchedule()
            }
        }

        parseSchedule()
    }

    fun parseSchedule() {
        log.d("SchedulerViewModel") { "parseSchedule: periodState: ${periodState.value} filteredJobs: ${filteredJobs.size}" }
        viewModelScope.launch {
            listOfPlans.clear()
            currentJobs.clear()
            monthSelectedDay.value = null
            ScheduleUtil.getPlanList(
                jobs = filteredJobs,
                periodType = periodState.value,
                selectedDay = selectedDay,
                weekSelectionStart = weekSelectionStart.value,
                weekSelectionEnd = weekSelectionEnd.value,
                monthSelectionLocalDate = monthSelectionLocalDate.value,
                listOfPlans,
                employeeId = if (isEmployee()) repository.getUser()?._id else null
            )
//            if (isEmployee())
//                ScheduleUtil.filterEmployeeJobs(repository.getUser()!!.getName(), listOfPlans)
        }
    }

    fun previous() {
        when (periodState.value) {
            PeriodType.DAY -> {
                selectedDay = selectedDay.minus(1, DateTimeUnit.DAY)
            }

            PeriodType.WEEK -> {
                weekSelectionStart.value = weekSelectionStart.value.minus(DatePeriod(days = 7))
                weekSelectionEnd.value = weekSelectionEnd.value.minus(DatePeriod(days = 7))
            }

            PeriodType.MONTH -> {
                monthSelectionLocalDate.value =
                    monthSelectionLocalDate.value.minus(DatePeriod(months = 1))
            }
        }
    }

    fun next() {
        when (periodState.value) {
            PeriodType.DAY -> {
                selectedDay = selectedDay.plus(1, DateTimeUnit.DAY)
            }

            PeriodType.WEEK -> {
                weekSelectionStart.value = weekSelectionStart.value.plus(DatePeriod(days = 7))
                weekSelectionEnd.value = weekSelectionEnd.value.plus(DatePeriod(days = 7))
            }

            PeriodType.MONTH -> {
                monthSelectionLocalDate.value =
                    monthSelectionLocalDate.value.plus(DatePeriod(months = 1))
            }
        }
    }

    fun periodChanged() {
        viewModelScope.launch {
            when (periodState.value) {
                PeriodType.DAY -> {
                    if (selectedDay == today) {
                        currentPeriod.value = stringResourcePlain(Res.string.today)
                    } else {
                        currentPeriod.value = getCurrentDayString()
                    }
                }

                PeriodType.WEEK -> {
                    currentPeriod.value = getCurrentWeekString()
                }

                PeriodType.MONTH -> {
                    currentPeriod.value = getCurrentMonthString()
                }
            }
        }
    }

    fun getCurrentDayString(): String {
        val date = selectedDay
        val month =
            date.month.name.take(3).lowercase()
                .replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() } // Abbreviation (e.g., "Sep")
        val day = date.dayOfMonth
        val year = date.year

        return "$day $month $year"
    }

    fun getCurrentWeekString(): String {
        return DateTimeUtil.getFormattedWeekString(weekSelectionStart.value, weekSelectionEnd.value)
    }

    fun getCurrentMonthString(): String {
        val month = monthSelectionLocalDate.value.month.name.take(3).lowercase()
            .replaceFirstChar { it.uppercase() } // e.g., "January"
        val year = monthSelectionLocalDate.value.year
        return "$month, $year"
    }

    fun filterCurrentJobs(eventList: List<Event>) {
        val jobIdsInEvents = eventList.map { it.jobId }.toSet()
        currentJobs.clear()
        currentJobs.addAll(jobList.filter { it.id in jobIdsInEvents })
    }

    fun clear() {
        initialLoad.value = true
        periodState.value = PeriodType.DAY
        currentPeriod.value = ""
        selectedDay = Clock.System.todayIn(TimeZone.currentSystemDefault())
        listOfPlans.clear()
        selectedJobWeeklyHover.value = false
        hoveredEvent.value = null
        position.value = Position(Dp(0f), Dp(0f))
        currentJobs.clear()
        monthSelectedDay.value = null

        val todayDate = Clock.System.todayIn(TimeZone.currentSystemDefault())
        weekSelectionStart.value = Clock.System.now()
            .toLocalDateTime(TimeZone.currentSystemDefault()).date.minus(DatePeriod(days = todayDate.dayOfWeek.ordinal))
        weekSelectionEnd.value = Clock.System.now()
            .toLocalDateTime(TimeZone.currentSystemDefault()).date.plus(DatePeriod(days = DayOfWeek.SUNDAY.ordinal - todayDate.dayOfWeek.ordinal))
        monthSelectionLocalDate.value = Clock.System.todayIn(TimeZone.currentSystemDefault())
    }
}